// @core/stores/notifications/NotificationsContactStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { notificationsApi } from '@core/api';
import type {
  Contact,
  ContactResponse,
  CreateContactRequest,
  SendContactVerificationRequest,
  SetContactPrimaryRequest,
  SetContactVerifiedRequest,
  UpdateContactRequest,
  ValidateContactValueRequest,
  ValidateContactValueResponse,
} from '@core/types/notifications';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class NotificationsContactStore {
  // List state
  items: Contact[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Contact | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Contact>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Contact | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Contact | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create contact
   */
  async CreateContact(data: CreateContactRequest): Promise<Contact> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await notificationsApi.CreateContact(data);
      const item = response.contact;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete contact
   */
  async DeleteContact(contact_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await notificationsApi.DeleteContact(contact_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== contact_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === contact_id) {
          this.currentItem = null;
        }

        this.removeFromCache(contact_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get contact by ID
   */
  async GetContact(contact_id: string): Promise<Contact> {
    // Check cache first
    const cached = this.cache.get(contact_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await notificationsApi.GetContact(contact_id);
      const item = response.contact;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get contact by value
   */
  async GetContactByValue(params?: { }): Promise<ContactResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetContactByValue(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List contacts with filters
   */
  async ListContacts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListContacts(params);

      runInAction(() => {
        this.items = response.contacts?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List contacts for entity
   */
  async ListContactsByEntity(entity_type: string, entity_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListContactsByEntity(entity_type, entity_id, params);

      runInAction(() => {
        this.items = response.contacts?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send verification code to contact
   */
  async SendContactVerification(contact_id: string, data: SendContactVerificationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SendContactVerification(contact_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set contact as primary
   */
  async SetContactPrimary(contact_id: string, data: SetContactPrimaryRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SetContactPrimary(contact_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Verify contact with code
   */
  async SetContactVerified(contact_id: string, data: SetContactVerifiedRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SetContactVerified(contact_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update contact
   */
  async UpdateContact(contact_id: string, data: UpdateContactRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateContact(contact_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate contact value format
   */
  async ValidateContactValue(data: ValidateContactValueRequest): Promise<ValidateContactValueResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.ValidateContactValue(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const notificationsContactStore = new NotificationsContactStore();
